import { UserSegmentTypes } from './use-user-segments';

export type SegmentControlOption = { value: string; label: string };

export type SegmentControlConfig =
	| {
			segmentType: UserSegmentTypes;
			ux: 'dropdown';
			label: string;
			options: SegmentControlOption[];
			defaultLabel: string;
			getSelectedLabel?: (value: string | undefined, options: SegmentControlOption[]) => string;
	  }
	| {
			segmentType: UserSegmentTypes;
			ux: 'textbox';
			label: string;
			placeholder?: string;
			transformValue?: (value: string) => string;
	  }
	| {
			segmentType: UserSegmentTypes;
			ux: 'multi-select';
			label: string;
			options: SegmentControlOption[];
	  };

const JOB_FUNCTION_OPTIONS = [
	{ value: 'engineering', label: 'Engineering' },
	{ value: 'product management', label: 'Product Management' },
	{ value: 'design', label: 'Design' },
	{ value: 'data science', label: 'Data Science' },
	{ value: 'marketing', label: 'Marketing' },
	{ value: 'sales', label: 'Sales' },
	{ value: 'human resources', label: 'Human Resources' },
	{ value: 'finance', label: 'Finance' },
	{ value: 'operations', label: 'Operations' },
	{ value: 'legal', label: 'Legal' },
	{ value: 'customer success', label: 'Customer Success' },
	{ value: 'executive', label: 'Executive' },
	{ value: 'other', label: 'Other' },
	{ value: 'information technology', label: 'Information Technology' },
	{ value: 'customer service', label: 'Customer Service' },
	{ value: 'program management', label: 'Program Management' },
	{ value: 'c-suite', label: 'C-Suite' },
];

export const PRODUCT_OPTIONS = [
	{ value: 'jira-software', label: 'Jira' },
	{ value: 'confluence', label: 'Confluence' },
	{ value: 'jira-servicedesk', label: 'JSM' },
	{ value: 'loom', label: 'Loom' },
	{ value: 'jira-product-discovery', label: 'JPD' },
	{ value: 'rovo.ondemand', label: 'Rovo (On Demand)' },
	{ value: 'rovo', label: 'Rovo' },
];

export const COLLECTION_OPTIONS = [{ value: 'TWC', label: 'Teamwork Collection' }];

const OWNED_PRODUCT_OPTIONS = [
	{ value: 'jira-software', label: 'Jira' },
	{ value: 'confluence', label: 'Confluence' },
	{ value: 'bitbucket', label: 'Bitbucket' },
	{ value: 'compass', label: 'Compass' },
	{ value: 'jira-product-discovery', label: 'Jira Product Discovery' },
	{ value: 'jira-servicedesk', label: 'Jira Service Management' },
	{ value: 'statuspage', label: 'Statuspage' },
	{ value: 'loom', label: 'Loom' },
	{ value: 'rovo.ondemand', label: 'Rovo (On Demand)' },
	{ value: 'rovo', label: 'Rovo' },
	{ value: 'trello', label: 'Trello' },
];

const LOGIN_STATUS_OPTIONS = [
	{ value: 'loggedIn', label: 'Logged In' },
	{ value: 'loggedOut', label: 'Logged Out' },
];

const DOMAIN_TYPE_OPTIONS = [
	{ value: 'BUSINESS', label: 'Business' },
	{ value: 'PERSONAL', label: 'Personal' },
];

export const RECOMMENDATION_FIELDS = [
	{
		segmentType: UserSegmentTypes.FIRST_RECOMMENDATION,
		label: 'First Recommendation',
		defaultValue: 'jira',
		defaultLabel: 'Jira',
	},
	{
		segmentType: UserSegmentTypes.SECOND_RECOMMENDATION,
		label: 'Second Recommendation',
		defaultValue: 'confluence',
		defaultLabel: 'Confluence',
	},
	{
		segmentType: UserSegmentTypes.THIRD_RECOMMENDATION,
		label: 'Third Recommendation',
		defaultValue: 'jsm',
		defaultLabel: 'JSM',
	},
	{
		segmentType: UserSegmentTypes.FOURTH_RECOMMENDATION,
		label: 'Fourth Recommendation',
		defaultValue: 'loom',
		defaultLabel: 'Loom',
	},
] as const;

export const SEGMENT_CONTROL_CONFIGS: SegmentControlConfig[] = [
	{
		segmentType: UserSegmentTypes.LOGIN_STATUS,
		ux: 'dropdown',
		label: 'Login Status',
		options: LOGIN_STATUS_OPTIONS,
		defaultLabel: 'Logged Out',
		getSelectedLabel: (value, options) =>
			options.find((option) => option.value === value)?.label || 'Logged Out',
	},
	{
		segmentType: UserSegmentTypes.GEOLOCATION,
		ux: 'textbox',
		label: 'Geolocation',
		placeholder: 'e.g. US, UK, AU',
	},
	{
		segmentType: UserSegmentTypes.JOB_FUNCTION,
		ux: 'dropdown',
		label: 'Job Function',
		options: JOB_FUNCTION_OPTIONS,
		defaultLabel: 'Select job function',
		getSelectedLabel: (value, options) =>
			options.find((option) => option.value === value)?.label || 'Select job function',
	},
	{
		segmentType: UserSegmentTypes.DOMAIN_TYPE,
		ux: 'dropdown',
		label: 'Domain Type',
		options: DOMAIN_TYPE_OPTIONS,
		defaultLabel: 'Personal',
		getSelectedLabel: (value, options) =>
			options.find((option) => option.value === value)?.label || 'Personal',
	},
	{
		segmentType: UserSegmentTypes.OWNED_PRODUCTS,
		ux: 'multi-select',
		label: 'Owned Products',
		options: OWNED_PRODUCT_OPTIONS,
	},
	{
		segmentType: UserSegmentTypes.CLUSTER_TAG,
		ux: 'textbox',
		label: 'Anchor/Cluster Tag',
		transformValue: (value) => value.toLowerCase(),
	},
	{
		segmentType: UserSegmentTypes.SUB_ANCHOR,
		ux: 'textbox',
		label: 'Sub-Anchor',
		transformValue: (value) => value.toLowerCase(),
	},
	{
		segmentType: UserSegmentTypes.CHANNEL,
		ux: 'textbox',
		label: 'Channel',
		transformValue: (value) => value.toLowerCase(),
	},
	{
		segmentType: UserSegmentTypes.UTM_CONTENT,
		ux: 'textbox',
		label: 'UTM Content',
	},
	{
		segmentType: UserSegmentTypes.COLLECTION_OPTIONS,
		ux: 'multi-select',
		label: 'Collection Options',
		options: COLLECTION_OPTIONS,
	},
];
